import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { fetchCampusNewsList } from '../services/cms';

const formatDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${y}년 ${Number(m)}월 ${Number(d)}일`;
};

function NewsCard({ news, index }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });

  return (
    <Link
      ref={ref}
      to={`/news/${news.id}`}
      className={`group bg-primary-background rounded-[24px] desktop:rounded-[32px] shadow-lg overflow-hidden flex flex-col ${
        inView ? 'animate-fadeSlideUp' : ''
      }`}
      style={{ animationDelay: `${(index % 3) * 120}ms` }}
    >
      {news.imageUrl ? (
        <div className="overflow-hidden">
          <img
            src={news.imageUrl}
            alt={news.title}
            loading="lazy"
            className="w-full h-[200px] desktop:h-[240px] object-cover transition-transform duration-300 group-hover:scale-[1.04]"
          />
        </div>
      ) : (
        <div className="w-full h-[200px] desktop:h-[240px] bg-primary/10 flex items-center justify-center">
          <span className="kr-font text-primary/50 text-title-medium font-semibold">ANATOLE</span>
        </div>
      )}

      <div className="p-[20px] desktop:p-[28px] flex flex-col flex-grow">
        <span className="kr-font text-secondary-text text-label-medium">{formatDate(news.publishedAt)}</span>
        <h3 className="kr-font mt-[8px] text-primary-text font-semibold text-title-medium desktop:text-title-large leading-snug break-keep group-hover:text-primary transition-colors">
          {news.title}
        </h3>
        {news.preview && (
          <p className="kr-font mt-[10px] text-secondary-text text-body-medium desktop:text-body-large leading-relaxed break-keep flex-grow">
            {news.preview}
          </p>
        )}
        <span className="kr-font mt-[16px] text-primary text-label-large font-semibold">자세히 보기 →</span>
      </div>
    </Link>
  );
}

export default function NewsPage() {
  const { ref: heroRef, inView: heroInView } = useInView({ threshold: 0.3, triggerOnce: true });
  const [state, setState] = useState({ status: 'loading', news: [] });

  useEffect(() => {
    let alive = true;
    fetchCampusNewsList()
      .then((news) => alive && setState({ status: 'ready', news }))
      .catch(() => alive && setState({ status: 'error', news: [] }));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div>
      <section ref={heroRef} className="relative overflow-hidden bg-primary">
        <div className="absolute top-[14%] left-[8%] w-[210px] h-[210px] rounded-full bg-white/10 animate-float" />
        <div className="absolute bottom-[12%] right-[10%] w-[160px] h-[160px] rounded-full bg-white/10 animate-floatDelay" />
        <div className="absolute top-[46%] right-[27%] w-[110px] h-[110px] rounded-full bg-white/10 animate-floatSlow" />

        <div className="relative max-w-[1480px] mx-auto px-[20px] desktop:px-[50px] py-[70px] desktop:py-[120px] text-primary-background text-center">
          <h1
            className={`font-inter font-bold text-headline-medium desktop:text-display-medium ${
              heroInView ? 'animate-fadeSlideUp' : ''
            }`}
          >
            캠퍼스소식
          </h1>
          <p
            className={`kr-font mt-[16px] desktop:mt-[24px] text-title-small desktop:text-headline-small leading-relaxed desktop:leading-[46px] ${
              heroInView ? 'animate-fadeSlideUp' : ''
            }`}
          >
            아나톨레의 행사와 학습 이야기를 전해 드립니다.
          </p>
        </div>
      </section>

      <section className="bg-alternate px-[20px] desktop:px-[50px] py-[50px] desktop:py-[100px]">
        <div className="max-w-[1480px] mx-auto">
          {state.status === 'loading' && (
            <p className="kr-font text-center text-secondary-text text-body-large py-[60px]">불러오는 중입니다…</p>
          )}

          {state.status === 'error' && (
            <div className="bg-primary-background rounded-[24px] shadow-lg px-[24px] py-[50px] text-center">
              <p className="kr-font text-secondary-text text-body-large">
                소식을 불러오지 못했습니다.
                <br />
                잠시 후 다시 시도해 주세요.
              </p>
            </div>
          )}

          {state.status === 'ready' && state.news.length === 0 && (
            <div className="bg-primary-background rounded-[24px] shadow-lg px-[24px] py-[50px] text-center">
              <p className="kr-font text-secondary-text text-body-large">아직 등록된 소식이 없습니다.</p>
            </div>
          )}

          {state.status === 'ready' && state.news.length > 0 && (
            <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-3 gap-[20px] desktop:gap-[28px]">
              {state.news.map((news, i) => (
                <NewsCard key={news.id} news={news} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
